import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useHints } from './HintsProvider';

/** Delay before a hint appears for mouse hover or keyboard focus. */
export const HOVER_DELAY_MS = 400;
/** How long a finger must rest on an element before its hint appears. */
export const LONG_PRESS_MS = 500;
const HIDE_GRACE_MS = 200;
/** A finger that moves further than this is scrolling, not pressing. */
const MOVE_TOLERANCE_PX = 10;
const SELECTOR = '[data-feature-id]';
const BODY_CLASS = 'hints-on';

/** Text fields and editable areas: `?` is a character there, never a shortcut. */
const isTextEntry = (node: Element | null): boolean =>
  !!node && (node.matches('input, textarea, select') || !!node.closest('[contenteditable]:not([contenteditable="false"])'));

type Shown = { featureId: string; el: HTMLElement };

const featureElement = (target: EventTarget | null): HTMLElement | null =>
  target instanceof Element ? target.closest<HTMLElement>(SELECTOR) : null;

const isKeyboardFocus = (el: Element): boolean => {
  try {
    return el.matches(':focus-visible');
  } catch {
    return true; // very old engines: treat any focus as keyboard focus
  }
};

/**
 * One global layer: listens (event delegation) for any element with `data-feature-id` and shows
 * its hint in a small popover. Mouse: hover with a short delay. Keyboard: focus shows it, F1
 * or ? opens the manual section and Escape closes it (Tab keeps its normal job). Touch: long-press (the click that would follow is
 * swallowed, so pressing and holding never triggers the button). Renders nothing while the
 * user's setting is off. While on, `body.hints-on` switches off the iOS callout and text selection on
 * feature elements, so a long press shows the hint instead of the system menu.
 */
export function HintLayer() {
  const { enabled, texts } = useHints();
  const [shown, setShown] = useState<Shown | null>(null);
  const popoverId = useId();
  const keyHelpId = useId();
  const popoverRef = useRef<HTMLDivElement>(null);
  const textsRef = useRef(texts);
  textsRef.current = texts;
  const timers = useRef<{ show?: number; hide?: number; press?: number }>({});
  const shownRef = useRef<Shown | null>(null);
  shownRef.current = shown;
  const suppressClick = useRef(false);

  const clearTimers = useCallback(() => {
    window.clearTimeout(timers.current.show);
    window.clearTimeout(timers.current.hide);
    window.clearTimeout(timers.current.press);
  }, []);

  const hide = useCallback(() => {
    window.clearTimeout(timers.current.show);
    window.clearTimeout(timers.current.hide);
    setShown(null);
  }, []);

  const show = useCallback((el: HTMLElement) => {
    const featureId = el.dataset.featureId;
    if (!featureId || !textsRef.current[featureId]) return;
    window.clearTimeout(timers.current.hide);
    setShown({ featureId, el });
  }, []);

  const showAfter = useCallback(
    (el: HTMLElement, ms: number) => {
      window.clearTimeout(timers.current.show);
      window.clearTimeout(timers.current.hide);
      timers.current.show = window.setTimeout(() => show(el), ms);
    },
    [show],
  );

  const hideSoon = useCallback(() => {
    window.clearTimeout(timers.current.show);
    window.clearTimeout(timers.current.hide);
    timers.current.hide = window.setTimeout(hide, HIDE_GRACE_MS);
  }, [hide]);

  // Navigating away closes the popover.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    hide();
  }, [location.pathname, location.hash, hide]);

  // Switching hints off closes everything immediately.
  useEffect(() => {
    if (!enabled) {
      clearTimers();
      setShown(null);
    }
  }, [enabled, clearTimers]);

  // iOS: no system callout / selection on feature elements while hints are on (see styles.css).
  useEffect(() => {
    if (!enabled) return;
    document.body.classList.add(BODY_CLASS);
    return () => document.body.classList.remove(BODY_CLASS);
  }, [enabled]);

  // aria-describedby on the element while its hint is visible.
  useEffect(() => {
    if (!shown) return;
    const { el } = shown;
    const previous = el.getAttribute('aria-describedby');
    el.setAttribute('aria-describedby', previous ? `${previous} ${popoverId}` : popoverId);
    return () => {
      if (previous) el.setAttribute('aria-describedby', previous);
      else el.removeAttribute('aria-describedby');
    };
  }, [shown, popoverId]);

  useEffect(() => {
    if (!enabled) return;
    const insidePopover = (node: EventTarget | null) => node instanceof Node && !!popoverRef.current?.contains(node);

    let pressStart: { x: number; y: number } | null = null;

    const onPointerOver = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      if (insidePopover(e.target)) {
        window.clearTimeout(timers.current.hide);
        return;
      }
      const el = featureElement(e.target);
      if (!el) return;
      if (shownRef.current?.el === el) {
        window.clearTimeout(timers.current.hide);
        return;
      }
      showAfter(el, HOVER_DELAY_MS);
    };
    const onPointerOut = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      const from = featureElement(e.target);
      const leavingPopover = insidePopover(e.target);
      if (!from && !leavingPopover) return;
      const to = e.relatedTarget;
      if (from && to instanceof Node && from.contains(to)) return; // still inside the same element
      if (insidePopover(to)) return;
      if (leavingPopover && from === null && featureElement(to) === shownRef.current?.el) return;
      hideSoon();
    };

    const onFocusIn = (e: FocusEvent) => {
      if (insidePopover(e.target)) {
        window.clearTimeout(timers.current.hide);
        return;
      }
      const el = featureElement(e.target);
      if (!el || !isKeyboardFocus(el)) return;
      showAfter(el, HOVER_DELAY_MS);
    };
    const onFocusOut = (e: FocusEvent) => {
      const to = e.relatedTarget;
      const current = shownRef.current;
      if (insidePopover(to) || (current && to instanceof Node && current.el.contains(to))) return;
      if (featureElement(e.target) || insidePopover(e.target)) hideSoon();
    };

    // Tab is never intercepted: the popover is not in the Tab order. Keyboard users press F1 (or ?) on the
    // focused element - or, failing that, while its hint is shown - to open its manual section; Escape closes the hint.
    const openManual = (e: KeyboardEvent) => {
      const focused = featureElement(document.activeElement);
      const focusedFeature = focused?.dataset.featureId;
      const featureId = focusedFeature && textsRef.current[focusedFeature] ? focusedFeature : shownRef.current?.featureId;
      const entry = featureId ? textsRef.current[featureId] : undefined;
      if (!entry) return;
      e.preventDefault(); // F1: the browser's own help page
      hide();
      navigate(`/instrukcja#${entry.manualAnchor}`);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (!shownRef.current) return;
        e.preventDefault();
        hide();
      } else if (e.key === 'F1' && !e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey) {
        openManual(e);
      } else if (e.key === '?' && !e.ctrlKey && !e.altKey && !e.metaKey && !e.isComposing) {
        // Shift is allowed: on most keyboards ? is typed with it.
        const target = e.target instanceof Element ? e.target : null;
        if (isTextEntry(target) || isTextEntry(document.activeElement)) return;
        openManual(e);
      }
    };

    const onPointerDown = (e: PointerEvent) => {
      if (insidePopover(e.target)) return;
      if (e.pointerType === 'mouse') {
        window.clearTimeout(timers.current.show); // a click is not a hover
        if (shownRef.current) hide();
        return;
      }
      const el = featureElement(e.target);
      if (!el) {
        if (shownRef.current) hide(); // a tap elsewhere closes the hint
        return;
      }
      pressStart = { x: e.clientX, y: e.clientY };
      window.clearTimeout(timers.current.press);
      timers.current.press = window.setTimeout(() => {
        const featureId = el.dataset.featureId;
        if (!featureId || !textsRef.current[featureId]) return;
        suppressClick.current = true;
        show(el);
      }, LONG_PRESS_MS);
    };
    const cancelPress = () => {
      window.clearTimeout(timers.current.press);
      pressStart = null;
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!pressStart) return;
      if (Math.hypot(e.clientX - pressStart.x, e.clientY - pressStart.y) > MOVE_TOLERANCE_PX) cancelPress();
    };
    // The click after a long press must not activate the button or link underneath.
    const onClickCapture = (e: MouseEvent) => {
      if (!suppressClick.current) return;
      suppressClick.current = false;
      if (insidePopover(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
    };
    const onContextMenu = (e: Event) => {
      if (suppressClick.current && featureElement(e.target)) e.preventDefault(); // Android shows a menu on long-press
    };
    // A stale flag (finger lifted without a click, e.g. dragged away) must not eat the next real tap.
    const onPointerUp = () => {
      cancelPress();
      if (suppressClick.current) window.setTimeout(() => (suppressClick.current = false), 400);
    };
    const onScroll = () => {
      if (shownRef.current) hide();
    };

    document.addEventListener('pointerover', onPointerOver);
    document.addEventListener('pointerout', onPointerOut);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', cancelPress);
    document.addEventListener('click', onClickCapture, true);
    document.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerover', onPointerOver);
      document.removeEventListener('pointerout', onPointerOut);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', cancelPress);
      document.removeEventListener('click', onClickCapture, true);
      document.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('scroll', onScroll, true);
      clearTimers();
    };
  }, [enabled, hide, hideSoon, show, showAfter, clearTimers, navigate]);

  // Position next to the element once the popover has a size.
  const [position, setPosition] = useState<{ top: number; left: number; above: boolean } | null>(null);
  useLayoutEffect(() => {
    if (!shown || !popoverRef.current) {
      setPosition(null);
      return;
    }
    const rect = shown.el.getBoundingClientRect();
    const pop = popoverRef.current.getBoundingClientRect();
    const margin = 8;
    const gap = 8;
    const fitsBelow = rect.bottom + gap + pop.height + margin <= window.innerHeight;
    const above = !fitsBelow && rect.top - gap - pop.height >= margin;
    const top = above ? rect.top - gap - pop.height : rect.bottom + gap;
    const left = Math.min(Math.max(margin, rect.left + rect.width / 2 - pop.width / 2), window.innerWidth - pop.width - margin);
    setPosition({ top, left, above });
  }, [shown]);

  if (!enabled || !shown) return null;
  const entry = texts[shown.featureId];
  if (!entry) return null;
  return createPortal(
    <div
      ref={popoverRef}
      id={popoverId}
      role="tooltip"
      className="hint-pop"
      data-above={position?.above ? 'true' : undefined}
      style={{ top: position?.top ?? 0, left: position?.left ?? 0, visibility: position ? 'visible' : 'hidden' }}
    >
      <p className="hint-pop-text">{entry.hint}</p>
      <div className="hint-pop-more">
        <Link className="hint-pop-link" to={`/instrukcja#${entry.manualAnchor}`} tabIndex={-1} aria-describedby={keyHelpId}>
          Więcej w instrukcji
        </Link>
        <span className="hint-pop-key" aria-hidden="true" title="Na klawiaturze: naciśnij F1 lub ?, gdy ten element jest zaznaczony">
          F1 / ?
        </span>
        <span id={keyHelpId} className="visually-hidden">
          Naciśnij F1 lub ?, aby otworzyć instrukcję
        </span>
      </div>
    </div>,
    document.body,
  );
}
